import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { Loader } from "components/DesignSystem/Loader/Loader";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextArea } from "components/DesignSystem/TextArea/TextArea";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { CUSTOM_MESSAGE } from "constants/broadcast";
import { Form, Formik } from "formik";
import React, { useMemo, useState } from "react";
import { MultiValue, SingleValue } from "react-select";
import {
  TemplateTypes,
  useGetBroadcastTemplateQuery,
} from "store/apis/broadcast";
import {
  EmailPreview,
  EmptyMessagePreview,
  MessagePreview,
  processMessageBody,
} from "./BroadcastMessageTemplateMessages";
import classNames from "classnames";
import { broadcastMessageVariableSchema } from "formValidations/broadcastMessageVariableSchema";

const fieldKeys = {
  chat: "chat_template_variables",
  whatsapp: "whatsapp_template_variables",
  email: "email_template_variables",
};

const templateIdKeys = {
  whatsapp: "whatsapp_template_id",
  email: "email_template_id",
};

export const BroadcastMessageTemplateBody = ({
  type,
  handleGoBack,
  currentTemplateType,
  selectedStatesLength,
  setCurrentTemplateType,
  setTemplateFields,
  openSendBroadcast,
  templateFields,
}: {
  type: TemplateTypes;
  handleGoBack: () => void;
  currentTemplateType: number;
  selectedStatesLength: number;
  setCurrentTemplateType: (val: number) => void;
  setTemplateFields: React.Dispatch<
    React.SetStateAction<Record<string, Record<string, any> | string>>
  >;
  openSendBroadcast: () => void;
  templateFields: Record<string, Record<string, any> | string>;
}) => {
  const {
    data: template,
    isLoading,
    isFetching,
  } = useGetBroadcastTemplateQuery(
    {
      templateType: type,
    },
    {
      skip: !type,
    }
  );
  const [lastSelectedTemplateMap, setLastSelectedTemplateMap] = useState<
    Record<TemplateTypes, number>
  >({
    chat: 0,
    whatsapp: 0,
    email: 0,
  });
  const selectedTemplate: number = lastSelectedTemplateMap[type];

  const selectedTemplateVariables =
    template?.[selectedTemplate]?.template_variables;
  const customMessageIndex = template?.findIndex(
    (temp) => temp.template_id === CUSTOM_MESSAGE
  );
  const isCustomMessage =
    type === "chat" && selectedTemplate === customMessageIndex;

  const fieldNames = isCustomMessage
    ? ["message"]
    : Object.keys(selectedTemplateVariables || {}) || [];

  const [showWhatsappButton, setShowWhatsappButton] = useState(false);

  const allFieldValues = {
    ...((templateFields as Record<string, Record<string, any>>)[
      fieldKeys[type]
    ] || {}),
  };
  const initialValues = isCustomMessage
    ? { message: templateFields.message }
    : fieldNames.reduce(
        (acc: any, field: any) => ({
          ...acc,
          [field]: allFieldValues[field] || "",
        }),
        {}
      );

  const handleSubmit = (values: Record<string, any>) => {
    if (isCustomMessage) {
      setTemplateFields((prev) => ({
        ...prev,
        message: values.message,
      }));
    } else if (type === "chat") {
      let messageBody = fieldNames.reduce(
        (body, fieldName) =>
          body.replaceAll(
            `{{${fieldName}}}`,
            values[fieldName] || `{{${fieldName}}}`
          ),
        template?.[selectedTemplate]?.template_body || ""
      );
      setTemplateFields((prev) => ({
        ...prev,
        message: messageBody,
      }));
    } else {
      setTemplateFields((prev) => ({
        ...prev,
        [templateIdKeys[type]]: template?.[selectedTemplate]?.template_id || "",
        [fieldKeys[type]]: values,
      }));
    }
    if (currentTemplateType < selectedStatesLength) {
      setCurrentTemplateType(currentTemplateType + 1);
    } else {
      openSendBroadcast();
    }
  };

  const templateOptions = useMemo(() => {
    return (
      template?.map((temp: any, index: number) => ({
        label: temp.template_display_name,
        value: index.toString(),
      })) || []
    );
  }, [fieldNames]);

  if (isLoading || isFetching) {
    return (
      <Modal.Body className="t-flex t-gap-6 !t-p-0 !t-h-[500px] t-justify-center t-items-center">
        <Loader />
      </Modal.Body>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validateOnBlur
      validationSchema={broadcastMessageVariableSchema(fieldNames)}
    >
      {({ handleSubmit, values }) => {
        const buttonKey = fieldNames.find(
          (field: any) => selectedTemplateVariables?.[field]?.is_whatsapp_button
        );

        const messageBody = processMessageBody(
          template?.[selectedTemplate]?.template_body || "",
          fieldNames,
          values,
          type === "email",
          Boolean(buttonKey),
          buttonKey
        );

        const emailSubject = fieldNames
          .reduce(
            (body, fieldName) =>
              body.replaceAll(
                `{{${fieldName}}}`,
                values[fieldName] || `{{${fieldName}}}`
              ),
            template?.[selectedTemplate]?.template_subject || ""
          )
          .replaceAll("\r\n", "<br />");

        return (
          <Form onSubmit={handleSubmit} className="t-m-0">
            <Modal.Body className="t-flex t-gap-6 !t-p-0 !t-min-h-[480px] !t-max-h-[500px]">
              <div className="t-flex-1 t-pl-8 t-py-5">
                <Combobox
                  withForm
                  name="template"
                  label="Select a template to send"
                  required
                  menuPortalTarget={document.body}
                  options={templateOptions}
                  placeholder="Select"
                  onChange={(
                    selectedOption:
                      | MultiValue<OptionData>
                      | SingleValue<OptionData>
                  ) =>
                    setLastSelectedTemplateMap((prev) => ({
                      ...prev,
                      [type]:
                        Number(
                          (selectedOption as SingleValue<OptionData>)?.value
                        ) || 0,
                    }))
                  }
                  value={templateOptions[selectedTemplate] || null}
                />
                {fieldNames.length > 0 && (
                  <div className="t-text-subtext t-text-text-30 t-mt-6">
                    Fill out the details to send the broadcast
                  </div>
                )}

                <div
                  className={classNames("t-grid t-gap-5 t-my-4", {
                    "t-grid-cols-1": fieldNames.length === 1,
                    "t-grid-cols-2": fieldNames.length > 1,
                  })}
                >
                  {fieldNames.map((field: any) => {
                    if (field === "message") {
                      return (
                        <div className="t-col-span-full" key="messsage">
                          <TextArea
                            name="message"
                            required
                            label="Message"
                            placeholder="Enter custom message"
                            autoResize
                          />
                        </div>
                      );
                    }
                    return (
                      <TextInput
                        key={field}
                        name={field}
                        label={
                          selectedTemplateVariables?.[field].display_name || ""
                        }
                        placeholder="Enter"
                        required
                      />
                    );
                  })}
                </div>
              </div>
              <div className="t-flex-1 t-bg-surface-lighter-grey t-p-5 t-space-y-2 t-overflow">
                <div className="t-text-subtext-sm t-text-text-30">
                  Message Preview
                </div>

                {type === "email" ? (
                  <EmailPreview subject={emailSubject} body={messageBody} />
                ) : isCustomMessage && !values.message ? (
                  <EmptyMessagePreview />
                ) : (
                  <MessagePreview
                    body={isCustomMessage ? values.message : messageBody}
                  />
                )}
              </div>
            </Modal.Body>

            <Modal.FooterButtonGroup>
              <Button type="button" onClick={handleGoBack}>
                Go back
              </Button>
              <Button customType="primary" type="submit">
                Next
              </Button>
            </Modal.FooterButtonGroup>
          </Form>
        );
      }}
    </Formik>
  );
};
